import React, { useState, useMemo } from "react";
import { Button, Menu, Typography, Divider, Box, List, Paper, Chip } from "@mui/material";
import { ArrowDropDown, CalendarToday, History, LocationOn } from "@mui/icons-material";
import moment from "moment";

const ScheduleHistory = ({
  selectedDate,
  setSelectedDate,
  sublocations,
  selectedSublocation,
  setSelectedSublocation,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  // শুরুতে ০ দিন পেছনের হিস্ট্রি থাকবে (শুধুমাত্র আজকের দিন থেকে শুরু হবে)
  const [pastDayRange, setPastDayRange] = useState(0);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    // যদি চান মেনু বন্ধ করলে আবার রিসেট হয়ে যাক, তবে নিচের লাইনটি ব্যবহার করুন
    // setPastDayRange(0); 
  };

  const handleSelect = (subId, date) => {
    setSelectedDate(date);
    setSelectedSublocation(subId);
    handleMenuClose();
  };

  const handleLoadPrevious = (e) => {
    e.stopPropagation();
    // প্রথম ক্লিকে ৭ দিন আসবে, এরপর আরও ৭ দিন করে বাড়বে
    setPastDayRange((prev) => (prev === 0 ? 7 : prev + 7));
  };

  const dateRange = useMemo(() => {
    const dates = [];
    // -pastDayRange থেকে শুরু করে ভবিষ্যতের ৭ দিন পর্যন্ত
    for (let i = -pastDayRange; i <= 7; i++) {
      dates.push(moment().add(i, "days").format("YYYY-MM-DD"));
    }
    return dates.sort((a, b) => moment(a).diff(moment(b)));
  }, [pastDayRange]);

  const currentSubLocName = sublocations?.find((s) => s.id === selectedSublocation)?.name || "Select Area";

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        style={{ minWidth: 320 }}
        endIcon={<ArrowDropDown />}
        onClick={handleMenuOpen}
        sx={{ textTransform: "none", padding: "8px 16px", borderRadius: "8px", fontWeight: "bold" }}
      >
        {`${moment(selectedDate).format("DD/MM/YYYY")} - ${currentSubLocName}`}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: { maxHeight: 600, width: 420, borderRadius: "12px" },
        }}
      >
        {/* Sticky Header with Load Previous Request Button */}
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, bgcolor: "white", zIndex: 10, borderBottom: "1px solid #f1f5f9" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarToday fontSize="small" color="primary" /> Schedule History
          </Typography>
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<History />} 
            onClick={handleLoadPrevious}
            sx={{ 
                fontSize: "10px", 
                fontWeight: "bold", 
                borderRadius: "6px",
                textTransform: "none",
                borderColor: "divider"
            }}
          >
            {pastDayRange === 0 ? "Request Past Schedules" : "Load More Past Days"}
          </Button>
        </Box>

        <List sx={{ p: 0 }}>
          {dateRange.map((date) => {
            const isToday = date === moment().format("YYYY-MM-DD");
            const isSelectedDay = date === selectedDate;

            return (
              <Box 
                key={date} 
                sx={{ 
                  p: 1.5,
                  borderBottom: "1px solid #f8fafc",
                  '&:last-child': { borderBottom: 0 }
                }}
              >
                {/* Date Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                   <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 800, 
                      color: isToday ? "primary.main" : "#64748b",
                      fontSize: "11px",
                      letterSpacing: "0.5px"
                    }}
                  >
                    {moment(date).format("dddd, DD MMM YYYY").toUpperCase()}
                  </Typography>
                  {isToday && (
                    <Chip label="Today" size="small" color="primary" sx={{ height: 16, fontSize: '9px', ml: 1, fontWeight: 900 }} />
                  )}
                </Box>

                {/* Sublocation Grid */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {sublocations?.map((sub) => {
                    const isSelected = isSelectedDay && selectedSublocation === sub.id;
                    return (
                      <Box
                        key={`${date}-${sub.id}`}
                        onClick={() => handleSelect(sub.id, date)}
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          cursor: "pointer",
                          borderRadius: "20px",
                          border: "1.5px solid",
                          borderColor: isSelected ? "primary.main" : "#f1f5f9",
                          bgcolor: isSelected ? "primary.main" : "#f8fafc",
                          color: isSelected ? "white" : "#475569",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: isSelected ? "primary.main" : "white",
                            transform: "translateY(-1px)"
                          },
                        }}
                      >
                        <LocationOn sx={{ fontSize: 13, opacity: isSelected ? 1 : 0.4 }} />
                        <Typography sx={{ fontSize: "11.5px", fontWeight: 700 }}>
                          {sub.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </List>
      </Menu>
    </div>
  );
};

export default ScheduleHistory;